import gql from 'graphql-tag';
import I18n from 'i18n-js';
import React from 'react';
import { graphql, ChildProps } from 'react-apollo';
import { SHOP_PAGE } from '../shared/url_helpers';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { ListingsCollection } from '@reverbdotcom/commons/src/components/listings_collection';
import OverflowingRow from '@reverbdotcom/commons/src/components/overflowing_row';
import Tiles from '@reverbdotcom/commons/src/components/tiles';
import ListingCard from '@reverbdotcom/commons/src/components/listing_card';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import {
  CoreListingCombineAndSaveRow,
  core_apimessages_ShippingMethod,
} from '../../schemas/rql';

interface IExternalProps {
  categoryRootUuid?: string;
  excludedListingIds: string[];
  maxStandardRateDisplayCents?: number;
  shopName: string;
  shopId: string;
  shopSlug: string;
  trackingEntityId?: string;
  displayAddToCart?: boolean;
}

export type IProps = ChildProps<
IExternalProps,
CoreListingCombineAndSaveRow.Query,
CoreListingCombineAndSaveRow.Variables
>;

const filterListings = ({
  data,
  excludedListingIds,
  maxStandardRateDisplayCents,
}) => {
  const { loading, error, listingsSearch } = data;
  if (error || loading || !listingsSearch?.listings) { return []; }

  const excludedListingIdsSet = new Set(excludedListingIds);

  return listingsSearch.listings.filter((listing) => {
    if (excludedListingIdsSet.has(listing.id)) { return false; }

    const shippingPrices = listing.shipping?.shippingPrices || [];

    const standardShippingRate = shippingPrices.find(({ shippingMethod }) => shippingMethod === core_apimessages_ShippingMethod.SHIPPED);
    const combinedShippingRate = shippingPrices.find(({ shippingMethod }) => shippingMethod === core_apimessages_ShippingMethod.COMBINED_SHIPPING);

    if (!combinedShippingRate?.rate) { return false; }

    const standardRateCents = standardShippingRate?.rate?.amountCents;
    const combinedRateCents = combinedShippingRate.rate.amountCents;
    const validCombinableRate = (
      combinedRateCents < standardRateCents
      && (!maxStandardRateDisplayCents || standardRateCents <= maxStandardRateDisplayCents)
    );

    return validCombinableRate;
  });
};

function ViewAllCTA(props) {
  const shopPageUrl = SHOP_PAGE.expand({
    slug: props.shopSlug,
    query: { combined_shipping: true },
  });

  const clickEvent = {
    eventName: MParticleEventName.ClickedCombineAndSaveViewAll,
    shopSlug: props.shopSlug,
    listingId: props.listingId,
  };

  return (
    <CoreLink to={shopPageUrl} target="_blank" clickEvent={clickEvent}>
      {I18n.t('discovery.listingsRow.viewAll')}
    </CoreLink>
  );
}

export function CombineAndSaveRow({
  data,
  excludedListingIds = [],
  maxStandardRateDisplayCents,
  shopName,
  shopSlug,
  trackingEntityId,
  displayAddToCart,
}: IProps) {
  const filteredListings = filterListings({ data, excludedListingIds, maxStandardRateDisplayCents });
  const shouldDisplayRow = filteredListings.length > 0;
  if (!shouldDisplayRow) { return null; }

  return (
    <OverflowingRow
      title={I18n.t('discovery.listingsRow.combineAndSaveTitle')}
      ledeHtml={I18n.t('discovery.listingsRow.combineAndSaveLedeHtml', {
        shopName,
        tagOpen: '<div class="mb-1">',
        tagClose: '</div>',
      })}
      action={<ViewAllCTA shopSlug={shopSlug} listingId={trackingEntityId} />}
    >
      <Tiles singleRow fourWideMax placeholder={<ListingCard listing={null} />}>
        {filteredListings.map((listing, idx) => {
          return (
            <ListingCard
              listing={listing}
              key={listing.id}
              position={idx}
              combinedShippingDiscount
              displayAddToCart={displayAddToCart}
            />
          );
        })}
      </Tiles>
    </OverflowingRow>
  );
}

const withSearch = graphql<
IExternalProps,
CoreListingCombineAndSaveRow.Query,
CoreListingCombineAndSaveRow.Variables
>(
  gql`
    query Core_Listing_CombineAndSaveRow(
      $combinedShipping: Boolean
      $boostCombinedShippingCategories: [String]
      $shopId: String
    ) {
      listingsSearch(
        input: {
          combinedShipping: $combinedShipping
          boostCombinedShippingCategories: $boostCombinedShippingCategories
          shopId: $shopId
        }
      ) {
        listings {
          _id
          ...ListingsCollection
        }
      }
    }
    ${ListingsCollection.fragments.listing}
  `,
  {
    options: ({ categoryRootUuid, shopId }) => ({
      ssr: false,
      variables: {
        combinedShipping: true,
        boostCombinedShippingCategories: categoryRootUuid ? [categoryRootUuid] : [],
        shopId,
      },
    }),
  },
);

export default withSearch(CombineAndSaveRow);
